import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import set from 'lodash/set';
import startCase from 'lodash/startCase';
import update from 'lodash/update';
import values from 'lodash/values';
import log from '../../../logger';
import { errorGrowl, successGrowl } from '../../../store/rootThunks';
import { updateJob } from './index';
// Not from './index', which imports this file: see reducerName.js.
import { reducerName } from './reducerName';

const COMPLETE = 'completed';
const PEND = 'pending'; // eslint-disable-line
const INPROG = 'inProgress'; // eslint-disable-line
const FAIL = 'failed';

const thunk = (name, cb) => createAsyncThunk(`${reducerName}/${name}`, cb);
export const getJobs = thunk(`getJobs`, async (params, thunkAPI) => {
  const { getState, dispatch } = thunkAPI;
  try {
    const jobs = cloneDeep(get(getState(), `${reducerName}.jobs`, {}));
    // Poll any job that hasn't terminally finished
    const watching = values(jobs).filter((job) => [COMPLETE, FAIL].includes(job.status) === false);
    /* A job watched with a `statusUrl` is asked there, one by one: a plugin's own route,
       answering `{ job }` with the plugin's privilege. job-api needs cms.manageTenant, which
       some admins who start jobs do not have (the cohort member import, SURF-4879). The
       rest share one job-api call, made only when there are any. */
    const ids = watching.filter((job) => !job.statusUrl).map((job) => job.id);
    const found = [];
    if (ids.length > 0) {
      const { data } = await axios.get('job-api/getJobsStatuses', {
        params: { ids },
      });
      found.push(...get(data, 'jobs', []));
    }
    for (const watched of watching.filter((job) => job.statusUrl)) {
      try {
        const { data } = await axios.get(watched.statusUrl, { params: { jobId: watched.id } });
        if (data && data.job) found.push(data.job);
      } catch (e) {
        // Left as it was, so it is asked again next time.
        log.error(`Error getting job ${watched.id}`, e);
      }
    }

    found.forEach((job) => {
      const current = get(jobs, job.id);
      if (!current) {
        update(jobs, job.id, (c = {}) => ({ ...c, ...job }));
      } else if (current.status !== job.status) {
        const baseMessage = startCase(`${current.name || `Job: ${job.id}`} Has ${job.status}`);
        // A failure says why; a job that sets `successMessage` says what it did.
        const detail =
          job.status === FAIL
            ? get(job, 'params.errorMessage')
            : job.status === COMPLETE
            ? get(job, 'params.successMessage')
            : undefined;
        const growl = {
          groupId: 'globalroot',
          props: detail ? `${baseMessage}: ${detail}` : baseMessage,
        };
        if (job.status === COMPLETE) dispatch(successGrowl(growl));
        else if (job.status === FAIL) dispatch(errorGrowl(growl));
        set(jobs, `${job.id}.status`, job.status);
      }
    });
    const continueChecking = values(jobs).some(({ status }) => ![COMPLETE, FAIL].includes(status));
    return { jobs, continueChecking };
  } catch (e) {
    log.error('Error getting jobs', e);
    throw e;
  }
});

export const waitForJobCompletion = thunk(
  `waitForJobCompletion`,
  async ({ jobId, maxMs = 300_000, intervalMs = 2000 }, { dispatch }) => {
    try {
      const start = Date.now();
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const elapsed = Date.now() - start;
        if (elapsed > maxMs) throw new Error('Users import timed out.');
        const { data: resp } = await axios.get('job-api/getJob', { params: { jobId } });
        const job = resp?.job;
        if (job?.id) dispatch(updateJob({ job }));
        if (!job) throw new Error('Failed to load import job status.');
        if (job.status === 'completed') return job;
        if (job.status === 'failed') return job;
        await new Promise((r) => setTimeout(r, intervalMs));
      }
    } catch (e) {
      log.error('Error waiting for job completion', e);
      throw e;
    }
  }
);
